'use client';

import { Box, Typography } from '@mui/material';
import { radius } from '@odys/design-system/tokens/radius';
import Image from 'next/image';

import type { DestinationTile } from '@/app/api/lib/destinations/destination.types';
import { Link } from '@/configurations/i18n/navigation';
import { type NavTriggerContext, trackNavigationClick } from '@/configurations/tracking/navigation';

type Props = {
    tile: DestinationTile;
    onNavigate: () => void;
    triggerContext: NavTriggerContext;
    /** Hauteur de la zone image. Le nom s'affiche en dessous. */
    imageHeight?: number;
    /** Fond sombre — titre en blanc. */
    dark?: boolean;
};

export function DestinationMenuCard({
    tile,
    onNavigate,
    triggerContext,
    imageHeight = 100,
    dark = false,
}: Props) {
    return (
        <Box
            component={Link}
            href={`/destinations/${tile.slug}`}
            onClick={() => {
                trackNavigationClick({ navItem: 'destination_card', triggerContext });
                onNavigate();
            }}
            sx={{
                display: 'block',
                textDecoration: 'none',
            }}
        >
            {/* Zone image — shadow uniquement ici au hover */}
            <Box
                sx={{
                    position: 'relative',
                    height: imageHeight,
                    backgroundColor: 'secondary.main',
                    borderRadius: radius.lg,
                    overflow: 'hidden',
                    transition: 'transform 150ms ease, box-shadow 150ms ease',
                    '&:hover': {
                        transform: 'scale(1.02)',
                        boxShadow: 4,
                    },
                }}
            >
                {tile.imageUrl && (
                    <Image
                        src={tile.imageUrl}
                        alt={tile.name}
                        fill
                        style={{ objectFit: 'cover' }}
                        sizes="200px"
                    />
                )}
            </Box>

            {/* Titre en dessous */}
            <Typography
                variant="body2"
                sx={{
                    fontWeight: 500,
                    color: dark ? 'common.white' : 'text.primary',
                    mt: 0.75,
                    px: 0.25,
                    lineHeight: 1.3,
                }}
            >
                {tile.name}
            </Typography>
        </Box>
    );
}
